import { useToast, type SelectOption } from "@ir/ui";
import { AppConfig } from "@src/config/AppConfig";
import {
  ConnectAccountingOAuthUseCase,
  DisconnectAccountingOAuthUseCase,
  GetAccountingOAuthStatusUseCase,
  GetActiveAccountingSystemUseCase,
} from "@src/application/useCases/accountingOAuth";
import type { AccountingSystemLookupItem } from "@src/domain/dto/accountingSystems";
import { AccountingOAuthRepository } from "@src/infrastructure/repositories/accountingOAuth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const OAUTH_POPUP_NAME = "accounting_oauth";
const OAUTH_SUCCESS_MESSAGE = "ACCOUNTING_OAUTH_SUCCESS";
const OAUTH_ERROR_MESSAGE = "ACCOUNTING_OAUTH_ERROR";

const getApiOrigin = (): string => new URL(AppConfig.getApiUrl()).origin;

export interface UseBrandDetailsAccountingIntegrationParams {
  brandKey: string;
  items: AccountingSystemLookupItem[] | null;
  onConnectionStatusChange: (connected: boolean) => void;
  onStatusLoadingChange: (loading: boolean) => void;
  onSelectedSystemNameChange?: (systemName: string | null) => void;
}

export interface UseBrandDetailsAccountingIntegrationResult {
  selectedKey: string;
  options: SelectOption[];
  selectedOption: SelectOption | undefined;
  connecting: boolean;
  disconnecting: boolean;
  checkingStatus: boolean;
  actionsDisabled: boolean;
  connectButtonLabel: string;
  pendingKey: string | null;
  pendingOption: SelectOption | undefined;
  switching: boolean;
  onSelectedKeyChange: (key: string) => void;
  confirmSwitch: () => void;
  cancelSwitch: () => void;
  handleConnect: () => void;
  handleCheckConnectionStatus: () => void;
  handleForceDisconnect: () => void;
}

export function useBrandDetailsAccountingIntegration({
  brandKey,
  items,
  onConnectionStatusChange,
  onStatusLoadingChange,
  onSelectedSystemNameChange,
}: UseBrandDetailsAccountingIntegrationParams): UseBrandDetailsAccountingIntegrationResult {
  const { success: successToast, error: errorToast } = useToast();
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [connected, setConnected] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const popupMonitorRef = useRef<number | null>(null);
  const oauthHandledRef = useRef(false);

  const options = useMemo<SelectOption[]>(
    () =>
      (items ?? []).map((item) => ({
        value: item.accountingSystemKey,
        label: item.accountingSystemName,
      })),
    [items],
  );

  const selectedOption = options.find((option) => option.value === selectedKey);
  const pendingOption =
    pendingKey == null
      ? undefined
      : options.find((option) => option.value === pendingKey);
  const selectedSystemName =
    typeof selectedOption?.label === "string" ? selectedOption.label : null;
  const actionsDisabled =
    !selectedKey ||
    !brandKey ||
    connecting ||
    disconnecting ||
    checkingStatus ||
    switching;

  useEffect(() => {
    onSelectedSystemNameChange?.(selectedSystemName);
  }, [selectedSystemName, onSelectedSystemNameChange]);

  useEffect(() => {
    if (!brandKey || options.length === 0) return;

    let ignore = false;
    new GetActiveAccountingSystemUseCase(new AccountingOAuthRepository())
      .execute({ brandKey })
      .then(({ accountingSystemKey }) => {
        if (ignore || !accountingSystemKey) return;
        const match = options.find(
          (option) =>
            String(option.value).toUpperCase() ===
            accountingSystemKey.toUpperCase(),
        );
        if (match) {
          setSelectedKey(String(match.value));
        }
      })
      .catch(() => {
        // Leave dropdown unselected if active-system lookup fails.
      });

    return () => {
      ignore = true;
    };
  }, [brandKey, options]);

  const clearPopupMonitor = useCallback(() => {
    if (popupMonitorRef.current != null) {
      window.clearInterval(popupMonitorRef.current);
      popupMonitorRef.current = null;
    }
  }, []);

  const refreshStatus = useCallback(
    async (accountingSystemKey: string): Promise<boolean> => {
      if (!brandKey || !accountingSystemKey) {
        setConnected(false);
        onConnectionStatusChange(false);
        return false;
      }

      onStatusLoadingChange(true);
      try {
        const status = await new GetAccountingOAuthStatusUseCase(
          new AccountingOAuthRepository(),
        ).execute({
          brandKey,
          accountingSystemKey,
        });
        setConnected(status.connected);
        onConnectionStatusChange(status.connected);
        return status.connected;
      } catch (err) {
        setConnected(false);
        onConnectionStatusChange(false);
        throw err;
      } finally {
        onStatusLoadingChange(false);
      }
    },
    [brandKey, onConnectionStatusChange, onStatusLoadingChange],
  );

  useEffect(() => {
    if (!brandKey || !selectedKey) {
      setConnected(false);
      onConnectionStatusChange(false);
      return;
    }

    let ignore = false;
    onStatusLoadingChange(true);
    new GetAccountingOAuthStatusUseCase(new AccountingOAuthRepository())
      .execute({ brandKey, accountingSystemKey: selectedKey })
      .then((status) => {
        if (!ignore) {
          setConnected(status.connected);
          onConnectionStatusChange(status.connected);
          onStatusLoadingChange(false);
        }
      })
      .catch(() => {
        if (!ignore) {
          setConnected(false);
          onConnectionStatusChange(false);
          onStatusLoadingChange(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [brandKey, selectedKey, onConnectionStatusChange, onStatusLoadingChange]);

  useEffect(() => {
    return () => {
      clearPopupMonitor();
    };
  }, [clearPopupMonitor]);

  const handleConnect = async () => {
    if (
      !brandKey ||
      !selectedKey ||
      connecting ||
      disconnecting ||
      checkingStatus
    ) {
      return;
    }

    setConnecting(true);
    oauthHandledRef.current = false;

    // Must open before await — browsers block window.open after async work.
    const popup = window.open(
      "about:blank",
      OAUTH_POPUP_NAME,
      "width=720,height=780",
    );

    if (!popup) {
      errorToast({
        content:
          "Unable to open QuickBooks authorization window. Please allow popups and try again.",
      });
      setConnecting(false);
      return;
    }

    try {
      const { authorizationUrl } = await new ConnectAccountingOAuthUseCase(
        new AccountingOAuthRepository(),
      ).execute({
        brandKey,
        accountingSystemKey: selectedKey,
      });

      popup.location.href = authorizationUrl;

      const completeOAuth = async (
        outcome: "success" | "error" | "closed",
        errorMessage?: string,
      ) => {
        if (oauthHandledRef.current) return;
        oauthHandledRef.current = true;
        clearPopupMonitor();
        window.removeEventListener("message", onMessage);

        if (outcome === "error") {
          errorToast({
            content: errorMessage || "QuickBooks authorization failed.",
          });
        }

        try {
          const isConnected = await refreshStatus(selectedKey);
          if (outcome === "success") {
            if (isConnected) {
              successToast({
                content: "Accounting system connected successfully.",
              });
            } else {
              errorToast({
                content:
                  "Authorization finished, but connection status is not active yet.",
              });
            }
          }
        } catch (err) {
          errorToast({
            content:
              err instanceof Error
                ? err.message
                : "Failed to refresh accounting connection status.",
          });
        } finally {
          setConnecting(false);
        }
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== getApiOrigin() || event.source !== popup) {
          return;
        }

        const payload =
          event.data && typeof event.data === "object"
            ? (event.data as { type?: string; message?: string })
            : null;

        if (payload?.type === OAUTH_SUCCESS_MESSAGE) {
          void completeOAuth("success");
          return;
        }

        if (payload?.type === OAUTH_ERROR_MESSAGE) {
          void completeOAuth("error", payload.message);
        }
      };

      window.addEventListener("message", onMessage);
      clearPopupMonitor();
      popupMonitorRef.current = window.setInterval(() => {
        if (popup.closed) {
          void completeOAuth("closed");
        }
      }, 700);
    } catch (err) {
      clearPopupMonitor();
      if (!popup.closed) {
        popup.close();
      }
      errorToast({
        content:
          err instanceof Error
            ? err.message
            : "Failed to start accounting system connection.",
      });
      setConnecting(false);
    }
  };

  const handleCheckConnectionStatus = async () => {
    if (
      !brandKey ||
      !selectedKey ||
      connecting ||
      disconnecting ||
      checkingStatus
    ) {
      return;
    }

    setCheckingStatus(true);
    try {
      const isConnected = await refreshStatus(selectedKey);
      if (isConnected) {
        successToast({
          content: "Accounting system is connected.",
        });
      } else {
        errorToast({
          content: "Accounting system is not connected.",
        });
      }
    } catch (err) {
      errorToast({
        content:
          err instanceof Error
            ? err.message
            : "Failed to check accounting connection status.",
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleForceDisconnect = async () => {
    if (
      !brandKey ||
      !selectedKey ||
      connecting ||
      disconnecting ||
      checkingStatus
    ) {
      return;
    }

    setDisconnecting(true);
    try {
      await new DisconnectAccountingOAuthUseCase(
        new AccountingOAuthRepository(),
      ).execute({
        brandKey,
        accountingSystemKey: selectedKey,
      });

      const isConnected = await refreshStatus(selectedKey);
      if (isConnected) {
        errorToast({
          content:
            "Disconnect finished, but connection status is still active.",
        });
      } else {
        successToast({
          content: "Accounting system disconnected successfully.",
        });
      }
    } catch (err) {
      errorToast({
        content:
          err instanceof Error
            ? err.message
            : "Failed to disconnect accounting system.",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSelectedKeyChange = (key: string) => {
    if (key === selectedKey) return;
    if (!selectedKey) {
      setSelectedKey(key);
      return;
    }
    setPendingKey(key);
  };

  const cancelSwitch = () => {
    if (switching) return;
    setPendingKey(null);
  };

  const confirmSwitch = async () => {
    if (pendingKey == null || switching) return;

    if (!connected || !brandKey) {
      setSelectedKey(pendingKey);
      setPendingKey(null);
      return;
    }

    setSwitching(true);
    try {
      await new DisconnectAccountingOAuthUseCase(
        new AccountingOAuthRepository(),
      ).execute({
        brandKey,
        accountingSystemKey: selectedKey,
      });
      successToast({
        content: "Previous accounting system disconnected successfully.",
      });
      setSelectedKey(pendingKey);
      setPendingKey(null);
    } catch (err) {
      errorToast({
        content:
          err instanceof Error
            ? err.message
            : "Failed to disconnect accounting system.",
      });
      setPendingKey(null);
    } finally {
      setSwitching(false);
    }
  };

  let connectButtonLabel = "Connect";
  if (connecting) {
    connectButtonLabel = "Connecting...";
  } else if (connected) {
    connectButtonLabel = "Reconnect";
  }

  return {
    selectedKey,
    options,
    selectedOption,
    connecting,
    disconnecting,
    checkingStatus,
    actionsDisabled,
    connectButtonLabel,
    pendingKey,
    pendingOption,
    switching,
    onSelectedKeyChange: handleSelectedKeyChange,
    confirmSwitch: () => {
      void confirmSwitch();
    },
    cancelSwitch,
    handleConnect: () => {
      void handleConnect();
    },
    handleCheckConnectionStatus: () => {
      void handleCheckConnectionStatus();
    },
    handleForceDisconnect: () => {
      void handleForceDisconnect();
    },
  };
}
